<template>
  <div :class="`theme-form--dark credit-app-page credit-app-page--${status}`">
    <div class="card">
      <header class="card__header">
        <h2>Account Detail</h2>
      </header>

      <AccountForm :lang="lang" :formName="formName" @submit="update">
        <template v-slot:insideImages>
          <div style="display: flex; flex-wrap: wrap; justify-content: space-between;">
            <a v-for="img in insideImages" :key="img.id" :href="img.absolute_url" target="_blank">
              <img v-if="isImage(img.absolute_url)" :src="img.absolute_url" :alt="`${img.type}`" style="display: block; max-width: 25rem;" />
              <template v-else>{{ img.absolute_url }}</template>
            </a>
          </div>
        </template>
        <template v-slot:outsideImages>
          <div style="display: flex; flex-wrap: wrap; justify-content: space-between;">
            <a v-for="img in outsideImages" :key="img.id" :href="img.absolute_url" target="_blank">
              <img v-if="isImage(img.absolute_url)" :src="img.absolute_url" :alt="`${img.type}`" style="display: block; max-width: 25rem;" />
              <template v-else>{{ img.absolute_url }}</template>
            </a>
          </div>
        </template>

        <div class="row" v-if="application_id">
          <div class="col-md-12">
            <p><router-link :to="`/credit-dashboard/applications/${application_id}`">Goto existing credit application: {{ application_ccode }}</router-link></p>
          </div>
        </div>

        <div class="row">
          <div v-if="status !== 'pending'" class="col-md-12">
            <p>
              This new account was
              <strong>{{ status }}</strong> on
              <strong>{{ updated_at }}</strong> by
              <strong>{{ actioned_by_name }}</strong>.
            </p>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12 button-wrapper">
            <button class="btn btn-default" type="submit" :disabled="isLoading">Update</button>
            <button
              class="btn btn-dark"
              type="button"
              v-if="status !== 'pending'"
              @click="resend"
              :disabled="isLoading"
            >Re-send email</button>
            <button
              class="btn btn-danger"
              type="button"
              v-if="status !== 'declined'"
              @click="() => review('declined')"
              :disabled="isLoading"
            >Decline</button>
            <button
              class="btn btn-success"
              type="button"
              v-if="status !== 'approved'"
              @click="() => review('approved')"
              :disabled="isLoading"
            >Approve</button>
          </div>
        </div>
      </AccountForm>
    </div>
  </div>
</template>

<script>
  import axios, { RepositoryError } from "../../repositories";
  import { mapActions, mapGetters } from "vuex";
  import { notify, isImage } from "../../utils";

  import AccountForm from './AccountForm';

  export default {
    name: "AccountReview",
    components: {
      AccountForm,
    },
    mounted() {
      this.fetchAccount(this.id);
    },
    props: {
      id: {
        required: true,
        type: String|Number,
      },
      lang: {
        required: false,
        type: String,
        default: 'en',
      },
    },
    data() {
      return {
        formName: "b2b.new-accounts.review",
        status: "pending",
        updated_at: "",
        images: [],
        application_id: null,
        application_ccode: null,
        actioned_by_name: null,
      };
    },
    methods: {
      ...mapActions("forms", [
        "setStatus",
        "setErrors",
        "setFields",
      ]),
      async fetchAccount(id) {
        try {
          this.setStatus({
            form: this.formName,
            status: "loading",
          });

          const response = await axios.get(
            `/credit-dashboard/accounts/${id}`
          );
          const data = response.data;

          this.setFields({
            form: this.formName,
            fields: {...data},
          });

          this.status = data.status;
          this.updated_at = data.updated_at;
          this.images = response.data.images;

          if (data.application) {
            this.application_id = data.application.id;
            this.application_ccode = data.application.ccode;
          }
          if (data.actioned_by){
            this.actioned_by_name = data.actioned_by.name;
          }
        } catch (err) {
          if (err instanceof RepositoryError) {
            return;
          }

          throw err;
        }
        finally {
          this.setStatus({
            form: this.formName,
            status: "idle",
          });
        }
      },
      async update(fields) {
        try {
          this.setStatus({
            form: this.formName,
            status: "loading",
          });

          await axios.put(`/credit-dashboard/accounts/${this.id}`, {
            ...fields,
          });

          notify("Account details successfully updated", "success");
        } catch (err) {
          if (err instanceof RepositoryError) {
            return;
          }

          if (!err.response && !err.response.data.errors) {
            return;
          }

          this.setErrors({
            form: this.formName,
            errors: err.response.data.errors
          });
        } finally {
          this.setStatus({
            form: this.formName,
            status: "idle",
          });
        }
      },
      async resend() {
        try {
          this.setStatus({
            form: this.formName,
            status: "loading",
          });

          await axios.put(`/credit-dashboard/accounts/${this.id}/email`);

          notify(
            "Email re-send request has been received. The email should be re-sent in a few moments.",
            "success"
          );
        } catch (err) {
          if (err instanceof RepositoryError) {
            return;
          }

          throw err;
        } finally {
          this.setStatus({
            form: this.formName,
            status: "idle",
          });
        }
      },
      async review(verdict) {
        try {
          this.setStatus({
            form: this.formName,
            status: "loading",
          });

          const response = await axios.put(
            `/credit-dashboard/accounts/${this.id}/review`,
            {
              verdict
            }
          );

          this.status = verdict;
          this.updated_at = new Date().toLocaleString();
          notify(`This account has been ${verdict}`, "success");
          this.$router.back();
        } catch (err) {
          if (err instanceof RepositoryError) {
            return;
          }
          
          throw err;
        } finally {
          this.setStatus({
            form: this.formName,
            status: "idle",
          });
        }
      },
      isImage(url) {
        return isImage(url);
      },
    },
    computed: {
      ...mapGetters("forms", ["getStatus"]),
      isLoading() {
        return this.getStatus({ form: this.formName }) === "loading";
      },
      insideImages() {
        return this.images.filter(img => img.type === 'insideStore');
      },
      outsideImages() {
        return this.images.filter(img => img.type === 'outsideStore');
      },
    },
  };
</script>
